/**
 * Model for a dual.
 */
import { v4 as UUID } from 'uuid';
import { Division, Team } from '@reg/shared/model';
import { DualScoreAdjustment } from '@reg/shared/model/dual-score-adjustment.model';

export class Dual {
    public id: string = null;
    public isNew: boolean = true;
    public bottomTeam: Team = null;
    public topTeam: Team = null;
    public winnerTeamId: string = null;
    public division: Division = null;
    public eventId: string = null;
    public result: string = null;
    public winType: string = null;
    public isTopBye: boolean = null;
    public isBottomBye: boolean = null;
    public adjustments: DualScoreAdjustment[] = [];
    public dualNumber: string = null;
    public roundNameId: string = null;

    public static getFields(): string[] {
        return [
            'bottomTeam',
            'bottomTeamId',
            'topTeam',
            'topTeamId',
            'winnerTeamId',
            'division',
            'divisionId',
            'eventId',
            'result',
            'winType',
            'isTopBye',
            'isBottomBye',
            'adjustments',
            'dualNumber',
            'roundNameId',
        ];
    }

    constructor() {
        this.id = UUID();
    }

    public getLoserTeam(): Team {
        if (!this.winnerTeamId) {
            return null;
        }

        if (this.topTeam &&
            this.winnerTeamId === this.topTeam.id) {
            return this.bottomTeam;
        }

        return this.topTeam;
    }

    public getWinnerTeam(): Team {
        if (!this.winnerTeamId) {
            return null;
        }

        if (this.topTeam &&
            this.winnerTeamId === this.topTeam.id) {
            return this.topTeam;
        }

        return this.bottomTeam;
    }

    public clone(): Dual {
        const clone: Dual = new Dual();
        clone.id = this.id;
        clone.isNew = this.isNew;
        clone.bottomTeam = this.bottomTeam && this.bottomTeam.clone();
        clone.topTeam = this.topTeam && this.topTeam.clone();
        clone.winnerTeamId = this.winnerTeamId;
        clone.division = this.division && this.division.clone();
        clone.eventId = this.eventId;
        clone.result = this.result;
        clone.winType = this.winType;
        clone.isTopBye = this.isTopBye;
        clone.isBottomBye = this.isBottomBye;
        clone.adjustments = this.adjustments && this.adjustments.map((a: DualScoreAdjustment) => a.clone());
        clone.dualNumber = this.dualNumber;
        clone.roundNameId = this.roundNameId;

        return clone;
    }

    /**
     * Serialize to a json api format
     */
    public serialize(): string {
        const json: any = {};
        json.data = {};
        json.data.type = 'dual';
        json.data.id = this.id;
        json.data.attributes = {};
        json.data.relationships = {};
        json.data.attributes.result = this.result;
        json.data.attributes.winType = this.winType;
        json.data.attributes.isTopBye = this.isTopBye;
        json.data.attributes.isBottomBye = this.isBottomBye;
        json.data.attributes.dualNumber = this.dualNumber;

        if (this.bottomTeam) {
            json.data.relationships.bottomTeam = {
                data: { type: 'team', id: this.bottomTeam.id },
            };
        }
        if (this.topTeam) {
            json.data.relationships.topTeam = {
                data: { type: 'team', id: this.topTeam.id },
            };
        }
        json.data.relationships.winnerTeam = {
            data: { type: 'team', id: this.winnerTeamId },
        };
        if (this.division) {
            json.data.relationships.division = {
                data: { type: 'division', id: this.division.id },
            };
        }

        return JSON.stringify(json);
    }

    /**
     * Deserialize from a json api format
     */
    public deserialize(json: any, included: any[] = null): Dual {
        const data: any = json.data || json;
        const attrs: any = data.attributes || {};
        const relationships: any = data.relationships || {};
        included = included || json.included || [];

        this.id = data.id;
        this.isNew = false;
        this.eventId = attrs.eventId;
        this.winnerTeamId = attrs.winnerTeamId;
        this.result = attrs.result;
        this.winType = attrs.winType;
        this.isTopBye = attrs.isTopBye === true;
        this.isBottomBye = attrs.isBottomBye === true;
        this.dualNumber = attrs.dualNumber;
        this.roundNameId = attrs.roundNameId;
        this.bottomTeam = this.getTeamRelationship('bottomTeam', attrs, relationships, included);
        this.topTeam = this.getTeamRelationship('topTeam', attrs, relationships, included);

        if (relationships.division &&
            relationships.division.data &&
            relationships.division.data.id) {
            const divisionId: string = relationships.division.data.id;
            const division: any = included.find((i: any) => i.type === 'division' && i.id === divisionId);

            if (division) {
                this.division = (new Division(this.eventId)).deserialize(division, included);
            }
        } else if (attrs.divisionId) {
            this.division = new Division(attrs.eventId);
            this.division.id = attrs.divisionId;
        }

        if (relationships.adjustments &&
            relationships.adjustments.data) {
            this.adjustments = [];

            for (const adjustmentLink of relationships.adjustments.data) {
                const adjustment: any = included.find((i: any) => i.id === adjustmentLink.id);
                if (adjustment) {
                    this.adjustments.push((new DualScoreAdjustment()).deserialize(adjustment, included));
                }
            }
        }

        return this;
    }

    private getTeamRelationship(
        relationshipName: string,
        attributes: any,
        relationships: any[],
        included: any[],
    ): Team {
        const teamLink: any = relationships[relationshipName];
        if (teamLink &&
            included &&
            teamLink.data &&
            teamLink.data.id) {
            const teamId: string = teamLink.data.id;
            const team: any = included.find((i: any) => i.type === 'team' && i.id === teamId);

            if (team) {
                return (new Team(this.eventId)).deserialize(team, included);
            }
        } else if (attributes[`${relationshipName}Id`]) {
            return new Team(this.eventId, attributes[`${relationshipName}Id`], false);
        }

        return null;
    }
}
